import { request } from '@umijs/max';
import type {
  AssignEmployeeRolesRequest,
  AssignUserRolesRequest,
  BaseResponse,
  CreateEmployeeRequest,
  CreateRoleRequest,
  DeleteRoleRequest,
  EmployeeInfo,
  GetEmployeesRequest,
  GetEmployeesResponse,
  GetUsersRequest,
  GetUsersResponse,
  Permission,
  PermissionCheckRequest,
  PermissionCheckResponse,
  Role,
  UpdateEmployeeRequest,
  UpdateRoleRequest,
  UpdateUserPermissionRequest,
  UserInfo,
  UserRole,
} from '@/types';
import { getCookie } from '@/utils/cookie';

export type { EmployeeInfo, Permission, Role } from '@/types';

// API 基础配置
const API_BASE_URL = 'https://n8n.kuaizao.org/webhook/admin';

// API 路径配置
const API_PATHS = {
  // 获取当前用户权限和角色
  GET_USER_PERMISSION: 'permission/users',
  // 员工管理
  GET_EMPLOYEES: 'permission/employees',
  GET_EMPLOYEE_BY_ID: 'permission/employees/detail',
  CREATE_EMPLOYEE: 'permission/employees/create',
  UPDATE_EMPLOYEE: 'permission/employees/update',
  ASSIGN_EMPLOYEE_ROLES: 'permission/employees/assign-roles',
  DELETE_EMPLOYEE: 'permission/employees/delete',
  // 角色管理
  GET_ROLES: 'permission/roles/list',
  CREATE_ROLE: 'permission/roles/create',
  UPDATE_ROLE: 'permission/roles/update',
  DELETE_ROLE: 'permission/roles/delete',
  // 权限管理
  GET_PERMISSIONS: 'permission/permissions',
  ASSIGN_PERMISSIONS: 'permission/assign',
  CREATE_PERMISSION: 'permission/permissions/create',
  UPDATE_PERMISSION: 'permission/permissions/update',
  DELETE_PERMISSION: 'permission/permissions/delete',
};

// 权限服务命名空间
export namespace PermissionService {
  function getAuthHeaders(
    path?: string,
    token?: string,
    data?: Record<string, unknown>,
  ) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: token || getCookie('admin_access_token') || '',
    };

    if (path) {
      headers.path = path;
    }

    // 将数据放在headers中
    if (data) {
      Object.keys(data).forEach((key) => {
        headers[`data-${key}`] = String(data[key]);
      });
    }

    return headers;
  }

  // 检查用户权限
  export async function checkUserPermission(
    params: PermissionCheckRequest,
  ): Promise<PermissionCheckResponse> {
    return request<PermissionCheckResponse>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.GET_USER_PERMISSION, params.token, {
        email: params.email,
      }),
    });
  }

  // 获取用户列表 - 直接返回数组结构
  export async function getUsers(
    params: GetUsersRequest = {},
  ): Promise<UserInfo[]> {
    const response = await request(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.GET_USER_PERMISSION, undefined, {
        action: 'list',
        ...params,
      }),
    });
    // API直接返回数组，不包含data字段
    return Array.isArray(response) ? response : [];
  }

  // 获取员工列表
  export async function getEmployees(
    params: GetEmployeesRequest = {},
  ): Promise<GetEmployeesResponse> {
    const response = await request<any>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.GET_EMPLOYEES, undefined, {
        action: 'list',
        ...params,
      }),
    });

    // 根据新的API数据格式，优先处理直接返回数组的情况
    const rawEmployees: EmployeeInfo[] = Array.isArray(response)
      ? response
      : Array.isArray(response?.employees)
        ? response.employees
        : Array.isArray(response?.data?.employees)
          ? response.data.employees
          : Array.isArray(response?.data)
            ? response.data
            : [];

    // 合并多角色员工：SQL的LEFT JOIN + GROUP BY r.id会为每个角色产生一行
    // 按email合并（同一个人可能在kuaizao_employees表中有多条记录）
    const employeeMap = new Map<string, EmployeeInfo>();
    for (const emp of rawEmployees) {
      const empId = emp.employee_id ?? (emp as any).id;
      const key = emp.email;
      if (!key) continue; // 跳过无效记录
      const existing = employeeMap.get(key);
      if (existing) {
        // 合并角色：如果当前行有角色信息，追加到已有记录
        if (emp.role_name && emp.role_code) {
          const existingRoleNames = (existing.role_name || '')
            .split(', ')
            .filter(Boolean);
          const existingRoleCodes = (existing.role_code || '')
            .split(', ')
            .filter(Boolean);

          // 使用 Set 进行精确匹配去重
          const roleNameSet = new Set(existingRoleNames);
          const roleCodeSet = new Set(existingRoleCodes);

          if (!roleNameSet.has(emp.role_name)) {
            roleNameSet.add(emp.role_name);
            roleCodeSet.add(emp.role_code);
            existing.role_name = [...roleNameSet].join(', ');
            existing.role_code = [...roleCodeSet].join(', ');
          }
        }
        // 合并权限（去重）
        if (emp.permissions?.length) {
          const permSet = new Set([
            ...(existing.permissions || []),
            ...emp.permissions,
          ]);
          existing.permissions = [...permSet];
        }
        // 保留有效的employee_id和user_id
        if (!existing.employee_id && empId) existing.employee_id = empId;
        if (!existing.user_id && emp.user_id) existing.user_id = emp.user_id;
      } else {
        // 首次出现：如果角色为空则清理字段
        employeeMap.set(key, {
          ...emp,
          employee_id: empId,
          role_name: emp.role_name || undefined,
          role_code: emp.role_code || undefined,
          role_id: emp.role_id || undefined,
        });
      }
    }
    const employees = [...employeeMap.values()];

    const total = Number(
      response?.total ?? response?.data?.total ?? employees.length ?? 0,
    );

    return {
      employees,
      total,
    };
  }

  // 根据ID获取员工详情
  export async function getEmployeeById(
    employeeId: number,
  ): Promise<BaseResponse<EmployeeInfo>> {
    return request<BaseResponse<EmployeeInfo>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.GET_EMPLOYEE_BY_ID, undefined, {
        employee_id: employeeId,
      }),
    });
  }

  // 创建员工
  export async function createEmployee(
    data: CreateEmployeeRequest,
  ): Promise<BaseResponse<EmployeeInfo>> {
    return request<BaseResponse<EmployeeInfo>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.CREATE_EMPLOYEE, undefined, {
        action: 'create',
        ...data,
      }),
    });
  }

  // 更新员工信息
  export async function updateEmployee(
    data: UpdateEmployeeRequest,
  ): Promise<BaseResponse<EmployeeInfo>> {
    return request<BaseResponse<EmployeeInfo>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.UPDATE_EMPLOYEE, undefined, {
        action: 'update',
        ...data,
      }),
    });
  }

  // 为员工分配角色
  export async function assignEmployeeRoles(
    data: AssignEmployeeRolesRequest,
  ): Promise<BaseResponse<boolean>> {
    return request<BaseResponse<boolean>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.ASSIGN_EMPLOYEE_ROLES, undefined, {
        action: 'assign',
        ...data,
      }),
    });
  }

  export async function deleteEmployee(params: {
    employeeId: number;
    operatorEmail: string;
  }): Promise<BaseResponse<boolean>> {
    return request<BaseResponse<boolean>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.DELETE_EMPLOYEE, undefined, {
        action: 'delete',
        ...params,
      }),
    });
  }

  // 更新用户权限（实际上是分配用户角色的别名）
  export async function updateUserPermission(
    params: UpdateUserPermissionRequest,
  ): Promise<BaseResponse<boolean>> {
    return request<BaseResponse<boolean>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.ASSIGN_PERMISSIONS, undefined, {
        action: 'update',
        ...params,
      }),
    });
  }

  // 获取角色列表 - 支持分页请求
  export async function getRoles(
    params: { page?: number; pageSize?: number; search?: string } = {},
  ): Promise<Role[]> {
    const { page = 1, pageSize = 10, search = '' } = params;
    const response = await request<any>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.GET_ROLES, undefined, {
        action: 'list',
        page,
        pageSize,
        search,
      }),
    });

    // 根据新的API数据格式，优先处理直接返回数组的情况
    const roles: Role[] = Array.isArray(response)
      ? response
      : Array.isArray(response?.roles)
        ? response.roles
        : Array.isArray(response?.data?.roles)
          ? response.data.roles
          : Array.isArray(response?.data)
            ? response.data
            : [];

    return roles;
  }

  // 获取所有权限
  export async function getPermissions(): Promise<Permission[]> {
    const response = await request<any>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.GET_PERMISSIONS, undefined, {
        action: 'list',
      }),
    });

    // 根据用户提供的新数据格式，直接返回数组
    // 新的API返回格式是直接的数组，不再包装在对象中
    const permissions: Permission[] = Array.isArray(response)
      ? response
      : Array.isArray(response?.permissions)
        ? response.permissions
        : Array.isArray(response?.data?.permissions)
          ? response.data.permissions
          : Array.isArray(response?.data)
            ? response.data
            : [];

    return permissions;
  }

  // 创建角色
  export async function createRole(
    params: CreateRoleRequest,
  ): Promise<BaseResponse<Role>> {
    return request<BaseResponse<Role>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.CREATE_ROLE, undefined, {
        action: 'create',
        ...params,
      }),
    });
  }

  // 更新角色
  export async function updateRole(
    params: UpdateRoleRequest,
  ): Promise<BaseResponse<Role>> {
    return request<BaseResponse<Role>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.UPDATE_ROLE, undefined, {
        action: 'update',
        ...params,
      }),
    });
  }

  // 删除角色
  export async function deleteRole(
    params: DeleteRoleRequest,
  ): Promise<BaseResponse<boolean>> {
    return request<BaseResponse<boolean>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.DELETE_ROLE, undefined, {
        action: 'delete',
        ...params,
      }),
    });
  }

  // 分配用户角色
  export async function assignUserRoles(
    params: AssignUserRolesRequest,
  ): Promise<BaseResponse<boolean>> {
    return request<BaseResponse<boolean>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.ASSIGN_PERMISSIONS, undefined, {
        action: 'assign',
        ...params,
      }),
    });
  }

  export async function createPermission(
    params: Record<string, unknown>,
  ): Promise<BaseResponse<Permission>> {
    return request<BaseResponse<Permission>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.CREATE_PERMISSION, undefined, {
        action: 'create',
        ...params,
      }),
    });
  }

  export async function updatePermission(
    params: Record<string, unknown>,
  ): Promise<BaseResponse<Permission>> {
    return request<BaseResponse<Permission>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.UPDATE_PERMISSION, undefined, {
        action: 'update',
        ...params,
      }),
    });
  }

  export async function deletePermission(params: {
    permissionId: number;
    operatorEmail: string;
  }): Promise<BaseResponse<boolean>> {
    return request<BaseResponse<boolean>>(API_BASE_URL, {
      method: 'GET',
      headers: getAuthHeaders(API_PATHS.DELETE_PERMISSION, undefined, {
        action: 'delete',
        ...params,
      }),
    });
  }
}

// 导出便捷方法
export const {
  checkUserPermission,
  getUsers,
  updateUserPermission,
  getRoles,
  getPermissions,
  createRole,
  updateRole,
  deleteRole,
  assignUserRoles,
  getEmployees,
  getEmployeeById,
  createEmployee,
  updateEmployee,
  assignEmployeeRoles,
  deleteEmployee,
  createPermission,
  updatePermission,
  deletePermission,
} = PermissionService;
